import { deleteDoc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { normalizeStoredCountryCode } from '../constants/profileCountries';
import { normalizeSocialLinks, type SocialLinks } from '../constants/socialPlatforms';
import { sanitizeTeamTag, TEAM_TAG_REGEX } from '../features/teams/types';
import { publicCollectionDoc, publicDirectoryDoc } from '../firebase/collections';
import type { CollectionVisibility } from '../types';
import { isValidPlayerHandle } from './playerHandle';

export interface PublicCollectionProfile {
  displayName: string | null;
  photoURL: string | null;
  avatarUrl?: string | null;
  backgroundImageUrl?: string | null;
  bio?: string | null;
  socialLinks?: SocialLinks;
  countryCode: string | null;
  /** Denormalized team tag for public profiles. `undefined` preserves existing on sync. */
  teamSlug?: string | null;
}

export function sanitizePlayerSlugBase(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/** Normalize a user-chosen handle before availability checks and writes. */
export function normalizePlayerHandle(handle: string): string {
  return handle.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 48);
}

/** Slug assigned on first sign-in; reuses stored slug on return visits. */
export function buildInitialPlayerSlug(opts: {
  uid: string;
  displayName?: string | null;
  email?: string | null;
  existingSlug?: string | null;
}): string {
  if (opts.existingSlug && opts.existingSlug.length > 0) return opts.existingSlug;
  const base =
    opts.displayName ||
    opts.email?.split('@')[0] ||
    opts.uid.slice(0, 8);
  const slug = sanitizePlayerSlugBase(base);
  return `${slug}-${opts.uid.slice(0, 6).toLowerCase()}`;
}

/**
 * Handle to offer a new player: their display name without the auto uid suffix.
 * Falls back to the already-assigned slug when that name is too short to be a handle.
 */
export function suggestClaimablePlayerHandle(opts: {
  displayName?: string | null;
  email?: string | null;
  autoSlug: string;
}): string {
  const base = sanitizePlayerSlugBase(opts.displayName || opts.email?.split('@')[0] || '');
  if (isValidPlayerHandle(base)) return base;
  return opts.autoSlug;
}

/** Client-side guard used before handle rename writes. */
export async function resolveAvailablePlayerHandle(handle: string, uid: string): Promise<string> {
  const clean = normalizePlayerHandle(handle);
  if (!clean) throw new Error('Choose a valid handle.');
  if (!(await isPublicSlugAvailable(clean, uid))) {
    throw new Error('That handle is already taken.');
  }
  return clean;
}

function clipNullableString(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

function normalizeFeaturedDeckId(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 128) return null;
  return trimmed;
}

/**
 * Shape a directory payload that always satisfies `validPublicDirectoryData`
 * in firestore.rules (length limits, allowed keys, normalized social map).
 */
export function buildPublicDirectoryPayload(opts: {
  uid: string;
  profile: PublicCollectionProfile;
  teamSlug: string | null;
  featuredDeckId?: string | null;
  /**
   * Cache of `publicCollections/{uid}.visibility === 'public'`, so rendering a
   * profile does not need a second read to answer a question that is false for
   * almost every user. `publicCollections` remains the security gate.
   */
  collectionIsPublic: boolean;
}): {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
  avatarUrl: string | null;
  backgroundImageUrl: string | null;
  bio: string;
  socialLinks: SocialLinks;
  countryCode: string | null;
  teamSlug: string | null;
  featuredDeckId: string | null;
  collectionIsPublic: boolean;
  updatedAt: ReturnType<typeof Timestamp.now>;
} {
  return {
    uid: opts.uid,
    displayName: clipNullableString(opts.profile.displayName, 120),
    photoURL: clipNullableString(opts.profile.photoURL, 2048),
    avatarUrl: clipNullableString(opts.profile.avatarUrl, 2048),
    backgroundImageUrl: clipNullableString(opts.profile.backgroundImageUrl, 2048),
    bio:
      typeof opts.profile.bio === 'string'
        ? opts.profile.bio.trim().slice(0, 300)
        : '',
    socialLinks: normalizeSocialLinks(opts.profile.socialLinks),
    countryCode: normalizeStoredCountryCode(opts.profile.countryCode),
    teamSlug: normalizeTeamSlug(opts.teamSlug),
    featuredDeckId: normalizeFeaturedDeckId(opts.featuredDeckId),
    collectionIsPublic: opts.collectionIsPublic === true,
    updatedAt: Timestamp.now(),
  };
}

/**
 * Keep `publicDirectory` + `publicCollections` in sync.
 *
 * Player profiles are always publicly discoverable — `publicDirectory/{slug}` is written
 * regardless of collection visibility.  Only `publicCollections/{uid}.visibility` changes
 * between 'public' and 'private', which controls whether the card collection is browsable.
 *
 * The privacy gate (`publicCollections`) is written first so a directory validation
 * failure cannot leave collection visibility half-applied.
 *
 * On slug rename, the old slug's directory doc is removed.
 *
 * Pass `skipDirectoryWrite: true` when the account is being deleted to avoid
 * re-creating the directory entry after it has already been removed.
 */
export async function syncPublicCollectionListing(opts: {
  uid: string;
  slug: string;
  visibility: CollectionVisibility;
  profile: PublicCollectionProfile;
  previousSlug?: string;
  skipDirectoryWrite?: boolean;
}): Promise<void> {
  const slug = opts.slug.trim().toLowerCase();
  const prevSlug = opts.previousSlug?.trim().toLowerCase();

  if (!slug) return;

  // World-readable collection gate: only 'public' unlocks reads. 'team' stays private here.
  // Write this before the directory doc so privacy changes still land if directory fails.
  const publicGate = opts.visibility === 'public' ? 'public' : 'private';
  await setDoc(
    publicCollectionDoc(opts.uid),
    { slug, visibility: publicGate, updatedAt: Timestamp.now() },
    { merge: true }
  );

  if (opts.skipDirectoryWrite) return;

  const dirRef = publicDirectoryDoc(slug);
  // Preserve teamSlug / featuredDeckId when callers omit them so profile edits don't wipe them.
  let teamSlug: string | null;
  let featuredDeckId: string | null = null;
  const existing = await getDoc(dirRef);
  if (existing.exists()) {
    const ownerUid = existing.data()?.uid;
    // Stale slug owned by someone else — never overwrite; privacy gate already saved.
    if (typeof ownerUid === 'string' && ownerUid !== opts.uid) {
      console.warn(
        '[syncPublicCollectionListing] slug owned by another uid; skipping directory write',
        { slug, ownerUid, uid: opts.uid }
      );
      return;
    }
    const rawFeatured = existing.data()?.featuredDeckId;
    featuredDeckId = normalizeFeaturedDeckId(
      typeof rawFeatured === 'string' ? rawFeatured : null
    );
  }

  if (opts.profile.teamSlug === undefined) {
    if (existing.exists()) {
      const raw = existing.data()?.teamSlug;
      teamSlug = normalizeTeamSlug(typeof raw === 'string' ? raw : null);
    } else {
      teamSlug = null;
    }
  } else {
    teamSlug = normalizeTeamSlug(opts.profile.teamSlug);
  }

  // Full replace keeps the doc free of legacy keys that would fail hasOnly on merge writes.
  await setDoc(dirRef, buildPublicDirectoryPayload({
    uid: opts.uid,
    profile: opts.profile,
    teamSlug,
    featuredDeckId,
    collectionIsPublic: publicGate === 'public',
  }));

  // Remove the old slug's directory entry when the slug changes.
  // Do not swallow errors — a failed delete leaves duplicate directory rows
  // for the same uid (invite search, profile lookup by stale handle).
  if (prevSlug && prevSlug !== slug) {
    await deleteDoc(publicDirectoryDoc(prevSlug));
  }
}

/** Returns false when the slug is already owned by another user. */
export async function isPublicSlugAvailable(slug: string, uid: string): Promise<boolean> {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return false;
  const snap = await getDoc(publicDirectoryDoc(normalized));
  if (!snap.exists()) return true;
  return snap.data()?.uid === uid;
}

function normalizeTeamSlug(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const clean = sanitizeTeamTag(value);
  return TEAM_TAG_REGEX.test(clean) ? clean : null;
}

/** Patch only the denormalized team tag on a player's public directory entry. */
export async function syncPublicDirectoryTeamSlug(opts: {
  slug: string;
  teamSlug: string | null;
}): Promise<void> {
  const slug = opts.slug.trim().toLowerCase();
  if (!slug) return;
  const dirRef = publicDirectoryDoc(slug);
  const existing = await getDoc(dirRef);
  if (!existing.exists()) return;

  // Re-write a rules-safe full payload so legacy extra keys cannot fail hasOnly
  // on a merge-only patch.
  const data = existing.data() as Record<string, unknown>;
  const uid = typeof data.uid === 'string' ? data.uid : '';
  if (!uid) return;

  await setDoc(
    dirRef,
    buildPublicDirectoryPayload({
      uid,
      profile: {
        displayName: typeof data.displayName === 'string' ? data.displayName : null,
        photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
        avatarUrl: typeof data.avatarUrl === 'string' ? data.avatarUrl : null,
        backgroundImageUrl:
          typeof data.backgroundImageUrl === 'string' ? data.backgroundImageUrl : null,
        bio: typeof data.bio === 'string' ? data.bio : '',
        socialLinks: normalizeSocialLinks(data.socialLinks),
        countryCode: normalizeStoredCountryCode(data.countryCode),
      },
      teamSlug: opts.teamSlug,
      featuredDeckId: normalizeFeaturedDeckId(
        typeof data.featuredDeckId === 'string' ? data.featuredDeckId : null
      ),
      // Joining a team says nothing about collection visibility, so carry the
      // stored value rather than resetting someone's public collection to
      // private in the read cache.
      collectionIsPublic: data.collectionIsPublic === true,
    })
  );
}
